import { useState } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import { variant } from 'styled-system';

import i18n from '../../../app/i18n';
import useDropdown from '../../../lib/hooks/useDropdown';
import DateInput from '../../../shared/DateInput';
import Text from '../../../shared/Text';
import { Button } from '../../../shared/FormStyles';
import useDateInput from '../../../shared/DateInput/useDateInput';
import defaultAttrs from '../../../lib/utils/defaultAttrs';

const createDateRange = (startDate, endDate) =>
  startDate
    ? `${startDate} - ${endDate}`
    : `${i18n.t(
        'reports:components.datePicker.defaultStartDate',
      )} - ${endDate}`;

const DatePicker = ({
  initialDates,
  setSelectedDates,
  datePickerVariant = 'containedBlue',
}) => {
  const {
    startInputValue,
    endInputValue,
    activeFilter,
    dates,
    onDateChange,
    onInputChange,
    onFilterChange,
    isValid,
  } = useDateInput({
    initialDates,
  });
  const {
    dropdownRef,
    buttonRef,
    showDropdown,
    setShowDropdown,
  } = useDropdown();
  const { t } = useTranslation('reports');
  const [dateRange, setDateRange] = useState(
    createDateRange(startInputValue, endInputValue),
  );

  return (
    <DateRangeWrapper>
      <DateRange
        ref={buttonRef}
        onClick={() => setShowDropdown(state => !state)}
        variant={datePickerVariant}
      >
        <CalendarIcon />
        <Text variant="button" color="inherit">
          {dateRange}
        </Text>
        <ChevronIcon $showDatePicker={showDropdown} />
      </DateRange>
      {showDropdown && (
        <DatePickerWrapper ref={dropdownRef}>
          <DateInput
            startInputValue={startInputValue}
            endInputValue={endInputValue}
            activeFilter={activeFilter}
            dates={dates}
            isValid={isValid}
            onDateChange={onDateChange}
            onInputChange={onInputChange}
            onFilterChange={onFilterChange}
          />
          <ButtonWrapper>
            <DatePickerButton
              variant="tertiary"
              onClick={() => setShowDropdown(false)}
            >
              {t('components.datePicker.closeButton')}
            </DatePickerButton>
            <DatePickerButton
              variant="containedBlue"
              onClick={() => {
                setShowDropdown(false);
                setDateRange(createDateRange(startInputValue, endInputValue));
                setSelectedDates(dates);
              }}
            >
              {t('components.datePicker.applyButton')}
            </DatePickerButton>
          </ButtonWrapper>
        </DatePickerWrapper>
      )}
    </DateRangeWrapper>
  );
};

export default DatePicker;

const DateRangeWrapper = styled.div`
  position: relative;
`;

const DateRange = styled(Button)`
  margin: 0 16px;
  padding: 0 16px;
  height: 40px;
  display: flex;
  align-items: center;

  ${variant({
    variants: {
      containedBlue: {
        'svg path': {
          stroke: 'white',
        },
        '&:not(:focus):hover svg': {
          path: {
            stroke: 'white',
          },
        },
      },
      tertiary: {
        'svg path': {
          stroke: 'black.80',
        },
        '&:not(:focus):hover svg': {
          path: {
            stroke: 'black.60',
          },
        },
      },
    },
  })}
`;

const CalendarIcon = styled.i.attrs(
  defaultAttrs({
    className: 'fa-regular fa-calendar fa-xl',
  }),
)`
  margin: 0 10px 0 0;
`;

const ChevronIcon = styled.i.attrs(
  defaultAttrs({
    className: 'fa-solid fa-chevron-up',
  }),
)`
  margin: 0 0 0 4px;
  transform: ${({ $showDatePicker }) =>
    $showDatePicker ? 'rotate(0deg)' : 'rotate(180deg)'};
`;

const DatePickerWrapper = styled.div`
  position: absolute;
  z-index: 10;
  border: 1px solid ${themeGet('colors.black.10')};
  border-radius: 8px;
  top: 44px;
  background: ${themeGet('colors.white')};
  left: 15px;
  padding: 0 0px 16px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 16px 16px 0;
`;

const DatePickerButton = styled(Button)`
  margin: 0;

  &:first-child {
    margin: 0 8px 0 0;
  }
`;
